import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
// Highcharts
import * as Highcharts from 'highcharts';
// Models
import { Pilot, Vehicle, Battery, VehicleLogbook } from '@mocca-technologies/model';

import { selectedValues } from '../../logbooks-menu/logbooks-menu.component';
import { TimeFilter } from '../../logbooks-time-filter/logbooks-time-filter.component';
// Services
import { LogbookService } from 'src/app/services/logbooks.service';
import { CoreService } from 'src/app/services/core.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-drone-summary',
  templateUrl: './drone-summary.component.html',
  styleUrls: ['./drone-summary.component.scss'],
  encapsulation: ViewEncapsulation.None,
})

export class DroneSummaryComponent implements OnInit {

  /**
   * Input variables
   */

  @Input() vehicleLogbook: VehicleLogbook;
  @Input() selectedValues: selectedValues;
  @Input() batteryList: Battery[] = [];
  @Input() pilotList: Pilot[] = [];
  @Input() vehicleList: Vehicle[] = [];

  /**
   * Output variables
   */

  @Output() timeFilterChange = new EventEmitter<TimeFilter>();
  @Output() selectedBatteryFromGraph = new EventEmitter<String>();
  @Output() selectedPilotFromGraph = new EventEmitter<String>();

  /**
   * Variables
   */

  selectedDrone: Vehicle = null;

  popoverOptions = {
    cssClass: 'popover-style'
  };

  pilotsChart: Highcharts.Chart;

  batteriesChart: Highcharts.Chart;

  constructor(
    public logbookService: LogbookService,
    private coreService: CoreService,
    private translateService: TranslateService
  ) {}

  /**
   * Gets the primary color from CSS variable
   */
  private getPrimaryColor(): string {
    const element = document.documentElement;
    const primaryColor = getComputedStyle(element).getPropertyValue('--ion-color-primary').trim();
    return primaryColor || '#0066FF';  // Fallback color
  }

  /**
   * Generates a color palette derived from primary color
   */
  private generateColorPalette(): string[] {
    const primaryColor = this.getPrimaryColor();
    const hslColor = this.hexToHsl(primaryColor);
    const palette: string[] = [];

    // Generate variations of the primary color
    for (let i = 0; i < 10; i++) {
      const lightness = 30 + (i * 5); // Vary lightness from 30 to 75
      palette.push(`hsl(${hslColor.h}, ${hslColor.s}%, ${lightness}%)`);
    }
    return palette;
  }

  /**
   * Converts hex color to HSL
   */
  private hexToHsl(hex: string): { h: number; s: number; l: number } {
    hex = hex.replace(/^#/, '');
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
      }
    }
    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    };
  }

  // #region --> Life Cycle Methods

  ngOnInit() {

    this.initializeCharts();
    this.translateService.onLangChange.subscribe(() => {
      if (this.batteriesChart || this.pilotsChart) {
        this.updateChartTitles();
      }
    });

  }

  // #endregion --> Life Cycle Methods

  // #region --> Highcharts Methods

  /**
   * This method initializes the charts
   */

  initializeCharts() {
    this.batteriesChart = Highcharts.chart('batteriesChartContainer', {
        colors: this.generateColorPalette(),
        chart: {
          type: 'pie'
        },
        title: {
          text: this.translateService.instant('PAGES.LOGBOOKS.VEHICLE_LOGBOOK.BATTERY_USAGE'),
          style: { color: 'var(--ion-color-primary)', fontWeight: 'normal' }
        },
        series: [{
          type: 'pie',
          name: this.translateService.instant('PAGES.LOGBOOKS.VEHICLE_LOGBOOK.FLIGHTS'),
          data: this.vehicleLogbook?.batteryUsageList?.map(battery => ({
            name: battery?.sn,
            y: battery?.numOperations
          }))
        }],
        time: {
          useUTC: false
        },
        credits: {
          enabled: false
        },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            point: {
              events: {
                click: (event) => {
                  this.selectBattery(event.point.name);
                }
              }
            },
          }
        },
        responsive: {
          rules: [
            {
              condition: {
                maxWidth: 100,
                maxHeight: 120
              },
              chartOptions: {
                chart: {
                  spacing: [5, 5, 5, 5],
                },
                title: {
                  style: { fontSize: '3px' },
                  y: 20,
                  margin: 0
                },
                legend: {
                  itemStyle: { fontSize: '5px' },
                  itemDistance: 2,
                  layout: 'vertical',
                  align: 'center',
                  verticalAlign: 'bottom'
                },
                plotOptions: {
                  pie: {
                    size: '85%',
                    dataLabels: {
                      enabled: false,
                    }
                  }
                }
              }
            },
            // Small screens
            {
              condition: {
                minWidth: 100,
                minHeight: 120,
                maxWidth: 173,
                maxHeight: 179
              },
              chartOptions: {
                chart: {
                  spacing: [5, 5, 5, 5],
                },
                title: {
                  style: { fontSize: '6px' },
                  y: 20,
                  margin: 0
                },
                legend: {
                  itemStyle: { fontSize: '10px' },
                  itemDistance: 2,
                  layout: 'vertical',
                  align: 'center',
                  verticalAlign: 'bottom'
                },
                plotOptions: {
                  pie: {
                    size: '85%',
                    dataLabels: {
                      enabled: false,
                    }
                  }
                }
              }
            },
            // Medium screens
            {
              condition: {
                minWidth: 163,
                minHeight: 180,
              },
              chartOptions: {
                chart: {
                  spacing: [5, 5, 5, 5]
                },
                title: {
                  style: { fontSize: '8px' }
                },
                legend: {
                  itemStyle: { fontSize: '12px' },
                  itemDistance: 2
                },
                plotOptions: {
                  pie: {
                    size: '88%',
                    dataLabels: {
                      style: { fontSize: '10px' },
                      distance: 2
                    }
                  }
                }
              }
            },
            // Large screens
            {
              condition: {
                minWidth: 220,
                minHeight: 198
              },
              chartOptions: {
                chart: {
                  spacing: [5, 5, 5, 5]
                },
                title: {
                  style: { fontSize: '14px' }
                },
                legend: {
                  itemStyle: { fontSize: '14px' },
                  itemDistance: 2
                },
                plotOptions: {
                  pie: {
                    size: '90%',
                    dataLabels: {
                      style: { fontSize: '12px' },
                      distance: 2
                    }
                  }
                }
              }
            }
          ]
        }
      });

      this.pilotsChart = Highcharts.chart('pilotsChartContainer', {
        colors: this.generateColorPalette(),
        chart: {
          type: 'pie'
        },
        title: {
          text: this.translateService.instant('PAGES.LOGBOOKS.VEHICLE_LOGBOOK.TOTAL_OPERATIONS_BY_PILOT'),
          style: { color: 'var(--ion-color-primary)', fontWeight: 'normal' }
        },
        series: [{
          type: 'pie',
          name: this.translateService.instant('PAGES.LOGBOOKS.VEHICLE_LOGBOOK.OPERATIONS'),
          data: this.vehicleLogbook?.operationsPerPilotList?.map(pilotRegister =>
            ({
              name: pilotRegister?.pilot?.username,
              y: pilotRegister?.numOperations
            }))
        }],
        time: {
          useUTC: false
        },
        credits: {
          enabled: false
        },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            point: {
              events: {
                click: (event) => {
                  this.selectPilot(event.point.name);
                }
              }
            },
          }
        },
        responsive: {
          rules: [
            {
              condition: {
                maxWidth: 100,
                maxHeight: 120
              },
              chartOptions: {
                chart: {
                  spacing: [5, 5, 5, 5],
                },
                title: {
                  style: { fontSize: '3px' },
                  y: 20,
                  margin: 0
                },
                legend: {
                  itemStyle: { fontSize: '5px' },
                  itemDistance: 0,
                  layout: 'vertical',
                  align: 'center',
                  verticalAlign: 'bottom'
                },
                plotOptions: {
                  pie: {
                    size: '85%',
                    dataLabels: {
                      enabled: false,
                    }
                  }
                }
              }
            },
            // Small screens
            {
              condition: {
                minWidth: 100,
                minHeight: 120,
                maxWidth: 173,
                maxHeight: 179
              },
              chartOptions: {
                chart: {
                  spacing: [5, 5, 5, 5],
                },
                title: {
                  style: { fontSize: '6px' },
                  y: 20,
                  margin: 0
                },
                legend: {
                  itemStyle: { fontSize: '7px' },
                  itemDistance: 0,
                  layout: 'vertical',
                  align: 'center',
                  verticalAlign: 'bottom'
                },
                plotOptions: {
                  pie: {
                    size: '85%',
                    dataLabels: {
                      enabled: false,
                    }
                  }
                }
              }
            },
            // Medium screens
            {
              condition: {
                minWidth: 163,
                minHeight: 180,
              },
              chartOptions: {
                chart: {
                  spacing: [5, 5, 5, 5]
                },
                title: {
                  style: { fontSize: '8px' }
                },
                legend: {
                  itemStyle: { fontSize: '8px' },
                  itemDistance: 0
                },
                plotOptions: {
                  pie: {
                    size: '88%',
                    dataLabels: {
                      style: { fontSize: '10px' },
                      distance: 2
                    }
                  }
                }
              }
            },
            // Large screens
            {
              condition: {
                minWidth: 220,
                minHeight: 198
              },
              chartOptions: {
                chart: {
                  spacing: [5, 5, 5, 5]
                },
                title: {
                  style: { fontSize: '14px' }
                },
                legend: {
                  itemStyle: { fontSize: '10px' },
                  itemDistance: 0
                },
                plotOptions: {
                  pie: {
                    size: '90%',
                    dataLabels: {
                      style: { fontSize: '12px' },
                      distance: 2
                    }
                  }
                }
              }
            }
          ]
        }
      });
  }

  /**
   * This method updates the charts data
   */

  updateCharts() {

    if (this.pilotsChart) {

      this.pilotsChart.update({
        xAxis: {
          categories: this.vehicleLogbook?.operationsPerPilotList?.map(pilotRegister => pilotRegister?.pilot?.username),
        },
        series: [{
          type: 'pie',
          name: this.translateService.instant('PAGES.LOGBOOKS.VEHICLE_LOGBOOK.OPERATIONS'),
          data: this.vehicleLogbook?.operationsPerPilotList?.map(pilotRegister =>
            ({
              name: pilotRegister?.pilot?.username,
              y: pilotRegister?.numOperations
            }))
        }]
      });
    }

    if (this.batteriesChart) {
      this.batteriesChart.update({
        series: [{
          type: 'pie',
          name: this.translateService.instant('PAGES.LOGBOOKS.VEHICLE_LOGBOOK.FLIGHTS'),
          data: this.vehicleLogbook?.batteryUsageList?.map(battery => ({
            name: battery?.sn,
            y: battery?.numOperations
          }))
        }]
      });
    }
  }

  selectBattery(batterySn: string) {

    if (batterySn && this.checkBatteryBelongsToUser(batterySn)) {
      this.selectedBatteryFromGraph.emit(batterySn);
    } else {
      this.coreService.showServerResponse('400', this.translateService.instant('PAGES.LOGBOOKS.VEHICLE_LOGBOOK.BAD_REQUEST_BATTERY'));
    }
  }

  selectPilot(username: string) {

    if (username && this.checkPilotBelongsToUser(username)) {
      this.selectedPilotFromGraph.emit(username);
    } else {
      this.coreService.showServerResponse('400', this.translateService.instant('PAGES.LOGBOOKS.VEHICLE_LOGBOOK.BAD_REQUEST_PILOT'));
    }
  }

  checkBatteryBelongsToUser(sn: string): boolean {
    let belongs = false;
    if (this.batteryList && this.batteryList.length > 0) {
      const battery = this.batteryList.find(b => b?.sn === sn);
      if (battery) {
        belongs = true;
      }
    }
    return belongs;
  }

  checkPilotBelongsToUser(username: string): boolean {
    let belongs = false;
    if (this.pilotList && this.pilotList.length > 0) {
      const pilot = this.pilotList.find(p => p?.username === username);
      if (pilot) {
        belongs = true;
      }
    }
    return belongs;
  }

  /**
   * Updates the chart titles when language changes
   */
  private updateChartTitles(): void {
    if (this.batteriesChart) {
      this.batteriesChart.update({
        title: {
          text: this.translateService.instant('PAGES.LOGBOOKS.VEHICLE_LOGBOOK.BATTERY_USAGE')
        },
        series: [{
          name: this.translateService.instant('PAGES.LOGBOOKS.VEHICLE_LOGBOOK.FLIGHTS')
        }]
      } as any, false);
      this.batteriesChart.redraw();
    }

    if (this.pilotsChart) {
      this.pilotsChart.update({
        title: {
          text: this.translateService.instant('PAGES.LOGBOOKS.VEHICLE_LOGBOOK.TOTAL_OPERATIONS_BY_PILOT')
        },
        series: [{
          name: this.translateService.instant('PAGES.LOGBOOKS.VEHICLE_LOGBOOK.OPERATIONS')
        }]
      } as any, false);
      this.pilotsChart.redraw();
    }
  }

  // #endregion --> Highcharts Methods

  // #region --> Time Filter Methods

  /**
   * This method updates the time filter
   * @param timeFilter
   */

  updateTimeFilter(timeFilter: TimeFilter) {
      this.timeFilterChange.emit(timeFilter);
  }

  // #endregion --> Time Filter Methods

  // #region --> Page Methods

  /**
   * This method checks if the summary data is available
   * @returns
   */
  summaryData(): boolean {
    let summaryData = false;

    if ( this.vehicleLogbook?.averageFlightTime > 0 && this.vehicleLogbook?.totalFlightTime > 0) {
      summaryData = true;
    }

    return summaryData;

  }

  // #endregion --> Page Methods

}
